import { last, isNull } from 'lodash-es'
import { zeroIfNaN } from './bits'

import envRaw from './env-raw'
import setCss from './set-css'
import StrictObj from './strict-obj'
import debugFields from './debug-fields'
import smoothScroll from './smooth-scroll'
import ScrollDetector from './scroll-detector'

import { fmtTranslate } from './fmt-translate'
import { offClick, onClickPassive } from './on-click'

const TOP = 'top'
const LEFT = 'left'
const WIDTH = 'width'
const HEIGHT = 'height'
const OPACITY = 'opacity'
const DISPLAY = 'display'
const TRANSFORM = 'transform'
const TRANSITION = 'transition'
const VISIBILITY = 'visibility'

const SCALE_ZERO = 'scale(0.2)'
const OPACITY_ZERO = 0
const DISPLAY_NONE = 'none'
const VISIBILITY_HIDDEN = 'hidden'
const VISIBILITY_VISIBLE = 'visible'

const SILENT_STR = 'silent'
const ANIMATE_STR = 'animate'

export default function MyIsotopeCarousel(
  transitionDuration,
  isoGridContentElm,
  isoGridShowcaseElm,
  carouselNextElm,
  carouselPrevElm,
  descDiffPairs,
  highlightPeriod,
  castToX1,
  castToX2,
  getCarouselSizerHeight,
  getCarouselHeight,
  getCarouselWidth,
  flowHeadInViewportHandler,
  flowTailInViewportHandler) {

  const transitionAnimate = `opacity ${transitionDuration}ms, transform ${transitionDuration}ms`
  const transitionHide = `opacity ${transitionDuration}ms, visibility 0ms ${transitionDuration}ms`
  const transitionShow = `opacity ${transitionDuration}ms`

  const scrollSpeed = envRaw.carouselScrollSpeed // px/ms
  const scrollEaseFunc = envRaw.carouselScrollEaseFunc

  const rowList = []
  const pairList = []
  const postList = []

  let animate = true // backward compatibility with my-isotope-masonry.js

  let skippedX2 = false
  let lastRowIndexX2 = null
  let prePositionCount = 0
  let highlightedCount = 0

  let sizerHeight = null
  let carouselWidth = null
  let carouselHeight = null

  const pendingMovePairs = []
  const pendingShowPairs = []

  let pendingMoveTimeout = null
  let pendingShowTimeout = null

  let firstActualPair = null
  let actualScrollLeft = 0

  let lastHeadInViewport = null
  let lastTailInViewport = null

  let lastCarouselHasNext = true
  let lastCarouselHasPrev = true

  const showcaseSD = ScrollDetector(isoGridShowcaseElm)
  showcaseSD.left.on(scrollHandler)

  setCss(isoGridContentElm, WIDTH, null)
  setCss(isoGridContentElm, HEIGHT, null)

  onClickPassive(carouselNextElm, carouselNext)
  onClickPassive(carouselPrevElm, carouselPrev)

  resetupFAR()
  resetupASL()
  resetupBaseValues()
  updateButtons()
  checkPosition()

  return StrictObj(debugFields({
    pairs,
    contains,
    scrollTo,
    silentMode,
    animateMode,
    currentMode,
    appendSilent,
    removeSilent,
    removeVirtual,

    carouselHasNext,
    carouselHasPrev,

    destroy,

    arrange,
    prepend,
    resize,
    append,
    remove
  }, {
    _showcaseSD: showcaseSD,

    _isoGridContentElm: isoGridContentElm,
    _isoGridShowcaseElm: isoGridShowcaseElm,
    _carouselNextElm: carouselNextElm,
    _carouselPrevElm: carouselPrevElm,
    _descDiffPairs: descDiffPairs,
    _highlightPeriod: highlightPeriod,
    _castToX1: castToX1,
    _castToX2: castToX2,
    _getCarouselSizerHeight: getCarouselSizerHeight,
    _getCarouselWidth: getCarouselWidth,
    _getCarouselHeight: getCarouselHeight,
    _flowHeadInViewportHandler: flowHeadInViewportHandler,
    _flowTailInViewportHandler: flowTailInViewportHandler,

    _scrollSpeed: scrollSpeed,
    _scrollEaseFunc: scrollEaseFunc,

    _rowList: rowList,
    _pairList: pairList,
    _postList: postList,

    _animate: () => animate,

    _skippedX2: () => skippedX2,
    _lastRowIndexX2: () => lastRowIndexX2,
    _prePositionCount: () => prePositionCount,
    _highlightedCount: () => highlightedCount,

    _sizerHeight: () => sizerHeight,
    _carouselWidth: () => carouselWidth,
    _carouselHeight: () => carouselHeight,

    _pendingMovePairs: pendingMovePairs,
    _pendingShowPairs: pendingShowPairs,

    _pendingMoveTimeout: () => pendingMoveTimeout,
    _pendingShowTimeout: () => pendingShowTimeout,

    _firstActualPair: () => firstActualPair,
    _actualScrollLeft: () => actualScrollLeft,

    _lastHeadInViewport: () => lastHeadInViewport,
    _lastTailInViewport: () => lastTailInViewport,

    _lastCarouselHasNext: () => lastCarouselHasNext,
    _lastCarouselHasPrev: () => lastCarouselHasPrev,

    _checkPosition: checkPosition,

    _updateButtons: updateButtons,
    _hideButton: hideButton,
    _showButton: showButton,

    _scrollHandler: scrollHandler,
    _displayAllPairs: displayAllPairs,
    _displayActivePairs: displayActivePairs,
    _calcIsFillViewport: calcIsFillViewport,
    _calcIsInViewport: calcIsInViewport,
    _calcIsActivePair: calcIsActivePair,
    _calcContentWidth: calcContentWidth,
    _calcIsAllPairsHasEqualWidth: calcIsAllPairsHasEqualWidth,

    _resetupFAR: resetupFAR,
    _resetupASL: resetupASL,
    _resetupBaseValues: resetupBaseValues,

    _syncFAR: syncFAR,
    _syncASL: syncASL,
    _getNextASL: getNextASL,
    _getPrevASL: getPrevASL,

    _scrollViewportToNextASL: scrollViewportToNextASL,
    _scrollViewportToPrevASL: scrollViewportToPrevASL,
    _scrollViewportToASL: scrollViewportToASL,
    _scrollViewportToZero: scrollViewportToZero,
    _scrollViewportToASLSilent: scrollViewportToASLSilent,
    _scrollViewportToZeroSilent: scrollViewportToZeroSilent,

    _adjustContentWidth: adjustContentWidth,
    _adjustContentHeight: adjustContentHeight,
    _prepareToMovePair: prepareToMovePair,
    _actuallyMovePairs: actuallyMovePairs,
    _actuallyMoveSilentPair: actuallyMoveSilentPair,
    _actuallyPlaceAndShow: actuallyPlaceAndShow,
    _actuallyPlaceAndNoneDisplayPair: actuallyPlaceAndNoneDisplayPair,
    _actuallyPlacePair: actuallyPlacePair,
    _actuallyShowPairs: actuallyShowPairs,
    _actuallyNoneDisplayPair: actuallyNoneDisplayPair,
    _actuallyDisplayPair: actuallyDisplayPair,
    _actuallyHidePair: actuallyHidePair,
    _virtuallyPlacePair: virtuallyPlacePair,
    _calcNeedHighlight: calcNeedHighlight,

    _carouselNext: carouselNext,
    _carouselPrev: carouselPrev
  }))

  // mains

  function pairs() {
    return pairList.concat().sort(descDiffPairs)
  }

  function destroy() {
    displayAllPairs()

    pairList.length = 0
    postList.length = 0

    showcaseSD.left.off(scrollHandler)

    offClick(carouselNextElm, carouselNext)
    offClick(carouselPrevElm, carouselPrev)

    setCss(isoGridContentElm, WIDTH, null)
    setCss(isoGridContentElm, HEIGHT, null)

    scrollViewportToZeroSilent()
  }

  function syncAll() {
    syncFAR()
    adjustContentWidth()
    adjustContentHeight()
    checkPosition()
    updateButtons()
  }

  function contains(post) {
    return postList.indexOf(post) !== -1
  }

  function scrollTo(post) {
    const postIndex = postList.indexOf(post)
    if (postIndex === -1) return

    const pair = pairList[postIndex]
    if (!pair.coord || !pair.size) return

    if (calcIsInViewport(pair)) return
    if (calcIsFillViewport(pair)) return

    const scrollLeft = Math.floor(pair.coord.x + pair.size.previewWidth / 2 - carouselWidth / 2)
    const scrollDuration = calcScrollDuration(actualScrollLeft, scrollLeft)
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function silentMode() {
    animate = false
  }

  function animateMode() {
    animate = true
  }

  function currentMode() {
    return animate
      ? ANIMATE_STR
      : SILENT_STR
  }

  function arrange() {
    postList.length = 0
    pairList.sort(descDiffPairs)

    resetupFAR()
    resetupASL()
    displayAllPairs()
    resetupBaseValues()

    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      postList.push(pair.post)
      const fromCoord = pair.coord
      pair.coord = virtuallyPlacePair(pair)
      prepareToMovePair(pair, fromCoord)
      deferMovePairs(pair)
    }

    syncAll()
    setTimeout(scrollViewportToZero, 33)
  }

  function prepend(pairs) {
    resetupBaseValues()

    for (let index = pairs.length - 1; index !== -1; index--) {
      const pair = pairs[index]
      pairList.unshift(pair)
      postList.unshift(pair.post)
    }

    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      pair.coord = virtuallyPlacePair(pair)

      const isActivePair = calcIsActivePair(pair)
      if (isActivePair) {
        pair.active = true
        actuallyPlacePair(pair)
        deferShowPairs(pair)
      } else {
        pair.active = false
        actuallyPlaceAndNoneDisplayPair(pair)
      }
    }

    for (let index = pairs.length, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      const fromCoord = pair.coord
      pair.coord = virtuallyPlacePair(pair)
      const isActivePair = calcIsActivePair(pair)
      if (isActivePair) {
        pair.active = true
        prepareToMovePair(pair, fromCoord)
        deferMovePairs(pair)
      } else {
        pair.active = false
        actuallyMoveSilentPair(pair)
      }
    }

    syncAll()
  }

  function resize() {
    displayAllPairs()
    resetupBaseValues()

    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      pair.coord = virtuallyPlacePair(pair)
      actuallyMoveSilentPair(pair)
    }

    adjustContentWidth()
    adjustContentHeight()

    syncASL()
    checkPosition()
    updateButtons()
    scrollViewportToASLSilent()
  }

  function carouselHasNext() {
    if (pairList.length === 0) {
      return false
    }

    const contentWidth = calcContentWidth()
    const hasNext = contentWidth > (actualScrollLeft + carouselWidth)
    return hasNext
  }

  function carouselNext() {
    if (!carouselHasNext()) {
      return
    }

    scrollViewportToNextASL()
  }

  function carouselHasPrev() {
    if (pairList.length === 0) {
      return false
    }

    const hasPrev = actualScrollLeft > 0
    return hasPrev
  }

  function carouselPrev() {
    if (!carouselHasPrev()) {
      return
    }

    scrollViewportToPrevASL()
  }

  function append(pairs) {
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      pairList.push(pair)
      postList.push(pair.post)
      pair.coord = virtuallyPlacePair(pair)

      const isActivePair = calcIsActivePair(pair)
      if (isActivePair) {
        pair.active = true
        actuallyPlacePair(pair)
        deferShowPairs(pair)
      } else {
        pair.active = false
        actuallyPlaceAndNoneDisplayPair(pair)
      }
    }

    syncAll()
  }

  function appendSilent(pairs) {
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      pairList.push(pair)
      postList.push(pair.post)
      pair.coord = virtuallyPlacePair(pair)

      const isActivePair = calcIsActivePair(pair)
      if (isActivePair) {
        pair.active = true
        actuallyPlaceAndShow(pair)
      } else {
        pair.active = false
        actuallyPlaceAndNoneDisplayPair(pair)
      }
    }

    syncAll()
  }

  function removeSilent(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        continue
      }

      const pair = pairList[postIndex]
      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
      actuallyNoneDisplayPair(pair)
    }

    syncAll()
  }

  function removeVirtual(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        return
      }

      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
    }
  }

  function remove(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        continue
      }

      const pair = pairList[postIndex]
      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
      actuallyHidePair(pair)
    }

    syncAll()
  }

  // helpers

  function checkPosition() {
    const headPosition = actualScrollLeft
    const tailPosition = calcContentWidth() - carouselWidth - actualScrollLeft

    const headInViewport = headPosition < carouselWidth / 2
    const tailInViewport = tailPosition < carouselWidth * 4

    if (lastHeadInViewport !== headInViewport) {
      lastHeadInViewport = headInViewport
      flowHeadInViewportHandler(headInViewport)
    }

    if (lastTailInViewport !== tailInViewport) {
      lastTailInViewport = tailInViewport
      flowTailInViewportHandler(tailInViewport)
    }
  }

  function updateButtons() {
    const currCarouselHasNext = carouselHasNext()
    if (currCarouselHasNext !== lastCarouselHasNext) {
      currCarouselHasNext
        ? showButton(carouselNextElm)
        : hideButton(carouselNextElm)
      lastCarouselHasNext = currCarouselHasNext
    }

    const currCarouselHasPrev = carouselHasPrev()
    if (currCarouselHasPrev !== lastCarouselHasPrev) {
      currCarouselHasPrev
        ? showButton(carouselPrevElm)
        : hideButton(carouselPrevElm)
      lastCarouselHasPrev = currCarouselHasPrev
    }
  }

  function showButton(buttonElm) {
    setCss(buttonElm, TRANSITION, transitionShow)
    setCss(buttonElm, VISIBILITY, VISIBILITY_VISIBLE)
    setCss(buttonElm, OPACITY, null)
  }

  function hideButton(buttonElm) {
    setCss(buttonElm, TRANSITION, transitionHide)
    setCss(buttonElm, VISIBILITY, VISIBILITY_HIDDEN)
    setCss(buttonElm, OPACITY, OPACITY_ZERO)
  }

  function scrollHandler(scrollLeft) {
    const contentWidth = calcContentWidth()

    if (scrollLeft < 0) {
      actualScrollLeft = 0
    } else if (scrollLeft >= (contentWidth - carouselWidth)) {
      actualScrollLeft = contentWidth - carouselWidth
    } else {
      actualScrollLeft = scrollLeft
    }

    syncFAR()
    checkPosition()
    updateButtons()
    displayActivePairs()
  }

  function displayAllPairs() {
    for (let index = 0; index !== pairList.length; index++) {
      const pair = pairList[index]
      if (!pair.active) {
        pair.active = true
        actuallyDisplayPair(pair)
      }
    }
  }

  function displayActivePairs() {
    for (let index = 0; index !== pairList.length; index++) {
      const pair = pairList[index]
      const isActivePair = calcIsActivePair(pair)
      if (isActivePair !== pair.active) {
        pair.active = isActivePair
        pair.active
          ? actuallyDisplayPair(pair)
          : actuallyNoneDisplayPair(pair)
      }
    }
  }

  function calcIsFillViewport(pair) {
    const pairX = pair.coord.x
    const pairEdge = pair.coord.x + pair.size.previewWidth

    const beforeLeft = pairX <= actualScrollLeft
    const afterRight = pairEdge >= actualScrollLeft + carouselWidth
    const fillViewport = beforeLeft && afterRight
    return fillViewport
  }

  function calcIsInViewport(pair) {
    const pairX = pair.coord.x
    const pairEdge = pair.coord.x + pair.size.previewWidth

    const tolerance = -1 * carouselWidth / 10
    const afterLeft = pairX >= actualScrollLeft - tolerance
    const beforeRight = pairEdge <= actualScrollLeft + carouselWidth + tolerance
    const isInViewport = afterLeft && beforeRight
    return isInViewport
  }

  function calcIsActivePair(pair) {
    const pairX = pair.coord.x
    const pairEdge = pair.coord.x + pair.size.previewWidth

    const tolerance = carouselWidth
    const isActivePair = true
      && pairX <= (actualScrollLeft + carouselWidth + tolerance)
      && pairEdge >= (actualScrollLeft - tolerance)

    return isActivePair
  }

  function calcIsAllPairsHasEqualWidth() {
    if (pairList.length === 0) {
      return false
    }

    const firstPair = pairList[0]
    const firstWidth = firstPair.size.previewWidth
    for (let index = 1, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      const pairWidth = pair.size.previewWidth
      if (firstWidth !== pairWidth) {
        return false
      }
    }
    return true
  }

  function calcContentWidth() {
    if (pairList.length === 0) {
      return 0
    }

    const lastPair = last(pairList)

    let contentWidth = lastPair.coord.x + lastPair.size.previewWidth
    for (let index = lastPair.coord.rowIndex - 1; index !== -1; index--) {
      const pair = pairList[pairList.length - 1 - index]
      const pairEdge = pair.coord.x + pair.size.previewWidth
      if (pairEdge > contentWidth) {
        contentWidth = pairEdge
      }
    }

    return contentWidth
  }

  function resetupFAR() {
    firstActualPair = null
  }

  function resetupASL() {
    actualScrollLeft = 0
  }

  function resetupBaseValues() {
    // reset animate flag
    animate = true
    // highlight flags
    skippedX2 = false
    lastRowIndexX2 = null
    prePositionCount = 0
    highlightedCount = 0
    // sizes
    sizerHeight = Math.max(1, getCarouselSizerHeight())
    carouselWidth = Math.max(1, getCarouselWidth())
    carouselHeight = Math.max(1, getCarouselHeight())
    // checks
    // rowList
    rowList.length = 0
    const rowNum = Math.max(1, Math.round(zeroIfNaN(carouselHeight / sizerHeight)))
    for (let index = 0; index !== rowNum; index++) {
      rowList[index] = 0
    }
  }

  function syncFAR() {
    firstActualPair = null
    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      const pairX = pair.coord.x
      if (pairX >= actualScrollLeft) {
        firstActualPair = pair
        break
      }
    }
  }

  function syncASL() {
    if (!firstActualPair) {
      actualScrollLeft = 0
      return
    }

    actualScrollLeft = firstActualPair.coord.x
  }

  function getNextASL() {
    if (pairList.length === 0) {
      return 0
    }

    if (calcIsAllPairsHasEqualWidth()) {
      const colsPerScreen = Math.max(1, Math.round(zeroIfNaN(carouselWidth / firstActualPair.size.previewWidth)))
      const colsToScroll = (firstActualPair.coord.x > actualScrollLeft) ? (colsPerScreen - 1) : colsPerScreen
      const contentWidth = calcContentWidth()
      const offsetWidth = Math.round(colsToScroll * firstActualPair.size.previewWidth)
      const nextASL = Math.min(contentWidth, firstActualPair.coord.x + offsetWidth)
      return nextASL
    } else {
      const contentWidth = calcContentWidth()
      const nextASL = Math.min(contentWidth, actualScrollLeft + carouselWidth)
      return nextASL
    }
  }

  function getPrevASL() {
    if (pairList.length === 0) {
      return 0
    }

    if (calcIsAllPairsHasEqualWidth()) {
      const colsPerScreen = Math.max(1, Math.round(zeroIfNaN(carouselWidth / firstActualPair.size.previewWidth)))
      const offsetWidth = Math.round(colsPerScreen * firstActualPair.size.previewWidth)
      const prevASL = Math.max(0, firstActualPair.coord.x - offsetWidth)
      return prevASL
    } else {
      const prevASL = Math.max(0, actualScrollLeft - carouselWidth)
      return prevASL
    }
  }

  function calcScrollDuration(posA, posB) {
    return Math.round(zeroIfNaN(Math.abs(posA - posB) / scrollSpeed))
  }

  function scrollViewportToNextASL() {
    const scrollLeft = getNextASL()
    const scrollDuration = calcScrollDuration(actualScrollLeft, scrollLeft)
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function scrollViewportToPrevASL() {
    const scrollLeft = getPrevASL()
    const scrollDuration = calcScrollDuration(actualScrollLeft, scrollLeft)
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function scrollViewportToASL() {
    const scrollLeft = actualScrollLeft
    const scrollDuration = 400
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function scrollViewportToZero() {
    const scrollLeft = 0
    const scrollDuration = calcScrollDuration(actualScrollLeft, scrollLeft)
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function scrollViewportToASLSilent() {
    const scrollLeft = actualScrollLeft
    const scrollDuration = 0
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function scrollViewportToZeroSilent() {
    const scrollLeft = 0
    const scrollDuration = 0
    smoothScroll.left(isoGridShowcaseElm, scrollLeft, scrollDuration, scrollEaseFunc)
  }

  function adjustContentWidth() {
    const maxRow = getMax(rowList)
    setCss(isoGridContentElm, WIDTH, maxRow)
  }

  function adjustContentHeight() {
    let maxCol = 0
    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      const edge = Math.round(pair.coord.y + pair.size.previewHeight)
      if (edge > maxCol) {
        maxCol = edge
      } else {
        break
      }
    }

    setCss(isoGridContentElm, HEIGHT, maxCol)
  }

  function prepareToMovePair(pair, fromCoord) {
    const moveCss = fmtTranslate(
      fromCoord.x - pair.coord.x,
      fromCoord.y - pair.coord.y)

    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, moveCss)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function deferMovePairs(pair) {
    pendingMovePairs.push(pair)
    if (isNull(pendingMoveTimeout)) {
      pendingMoveTimeout = setTimeout(actuallyMovePairs, 33)
    }
  }

  function cancelMovePair(post) {
    for (let index = pendingMovePairs.length - 1; index !== -1; index--) {
      const pair = pendingMovePairs[index]
      if (pair.post === post) {
        pendingMovePairs.splice(index, 1)
        break
      }
    }
  }

  function actuallyMovePairs() {
    const pairs = pendingMovePairs.concat()
    pendingMovePairs.length = 0
    pendingMoveTimeout = null
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      setCss(pair.elm, DISPLAY, null)
      setCss(pair.elm, TRANSITION, transitionAnimate)
      setCss(pair.elm, TRANSFORM, null)
      setCss(pair.elm, OPACITY, null)
    }
  }

  function actuallyMoveSilentPair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function actuallyPlaceAndShow(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function actuallyPlaceAndNoneDisplayPair(pair) {
    setCss(pair.elm, DISPLAY, DISPLAY_NONE)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function actuallyPlacePair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, SCALE_ZERO)
    setCss(pair.elm, OPACITY, OPACITY_ZERO)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function deferShowPairs(pair) {
    pendingShowPairs.push(pair)
    if (isNull(pendingShowTimeout)) {
      pendingShowTimeout = setTimeout(actuallyShowPairs, 33)
    }
  }

  function cancelShowPair(post) {
    for (let index = pendingShowPairs.length - 1; index !== -1; index--) {
      const pair = pendingShowPairs[index]
      if (pair.post === post) {
        pendingShowPairs.splice(index, 1)
        break
      }
    }
  }

  function actuallyShowPairs() {
    const pairs = pendingShowPairs.concat()
    pendingShowPairs.length = 0
    pendingShowTimeout = null
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      setCss(pair.elm, DISPLAY, null)
      setCss(pair.elm, TRANSITION, transitionAnimate)
      setCss(pair.elm, TRANSFORM, null)
      setCss(pair.elm, OPACITY, null)
    }
  }

  function actuallyNoneDisplayPair(pair) {
    setCss(pair.elm, DISPLAY, DISPLAY_NONE)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
  }

  function actuallyDisplayPair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
  }

  function actuallyHidePair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, transitionAnimate)
    setCss(pair.elm, TRANSFORM, SCALE_ZERO)
    setCss(pair.elm, OPACITY, OPACITY_ZERO)
  }

  function virtuallyPlacePair(pair) {
    prePositionCount++
    const rowIndex = getMinIndex(rowList)

    const hasRoomForX2 = true
      && rowList.length !== (rowIndex + 1)
      && rowList[rowIndex] === rowList[rowIndex + 1]

    const needHighlight = true
      && rowList.length >= 2
      && hasRoomForX2
      && calcNeedHighlight()

    const willHighlight = true
      && needHighlight
      && ((lastRowIndexX2 !== rowIndex) || (rowList.length === 2) || (skippedX2 === true))

    if (needHighlight) {
      skippedX2 = !willHighlight
    }

    pair.size = willHighlight
      ? castToX2(pair)
      : castToX1(pair)

    const rawSpan = Math.max(1, Math.round(zeroIfNaN(pair.size.previewHeight / sizerHeight)))
    const itemSpan = Math.min(rowList.length, rawSpan)

    const highlighted = true
      && willHighlight
      && itemSpan === 2

    if (highlighted) {
      lastRowIndexX2 = rowIndex
      highlightedCount++
    }

    const oldRow = rowList[rowIndex]
    const newRow = oldRow + pair.size.previewWidth
    const groupEdge = rowIndex + itemSpan

    for (let groupIndex = rowIndex; groupIndex !== groupEdge; groupIndex++) {
      rowList[groupIndex] = newRow
    }

    return StrictObj({
      x: oldRow,
      y: Math.round(sizerHeight * rowIndex),
      colIndex: Math.round(zeroIfNaN(oldRow / sizerHeight)),
      rowIndex,
      highlighted
    })
  }

  function calcNeedHighlight() {
    const highlight_period = highlightPeriod()
    if (highlight_period === null || highlight_period < 2) {
      return false
    }

    const offset = rowList.length <= 4
      ? highlight_period - 1
      : highlight_period - 3
    const count = prePositionCount + offset
    const expHighlighted = Math.floor(zeroIfNaN(count / highlight_period))

    return expHighlighted > highlightedCount
  }
}

function getMinIndex(arr) {
  if (arr.length === 0) {
    return 0
  }

  let min = arr[0]
  let res = 0
  for (let index = 1, length = arr.length; index !== length; index++) {
    const val = arr[index]
    if (val < min) {
      min = val
      res = index
    }
  }
  return res
}

function getMax(arr) {
  if (arr.length === 0) {
    return 0
  }

  let max = arr[0]
  for (let index = 1, length = arr.length; index !== length; index++) {
    const val = arr[index]
    if (val > max) {
      max = val
    }
  }
  return max
}
